<template>
  <Card border-color="primary">
    <CardTitle>
      <VText component="h2" variant="h5">Setup your form link</VText>
      <FlexSpacer />
      <IconButton
        size="1.25rem"
        :loading="isDeleting"
        title="Delete"
        aria-label="Delete"
        @click="$emit('remove')"
      >
        <CloseIcon />
      </IconButton>
    </CardTitle>
    <Divider color="primary" />
    <CardText>
      <ValidationObserver v-slot="{ invalid, handleSubmit }" ref="form" slim>
        <form @submit.prevent="handleSubmit(handleSetup)">
          <FormGroup :gutter-bottom="!!selectedSource">
            <Label component="span" block gutter-bottom
              >Choose your form destination</Label
            >
            <BoxInputGroup>
              <BoxInput
                v-for="(
                  { name, source, iconComponent, color }, index
                ) in FORM_TYPES"
                :key="source"
                v-model="selectedSource"
                :name="`form${linkId}`"
                :input-id="`form${index}${linkId}`"
                :input-value="source"
                :tooltip="name"
              >
                <component :is="iconComponent" slot="icon" :color="color" />
              </BoxInput>
            </BoxInputGroup>
          </FormGroup>

          <template v-if="selectedSource">
            <ValidationProvider
              v-slot="{ errors, ariaMsg, ariaInput }"
              ref="urlProvider"
              :rules="{
                required: true,
                url: true,
                validate_embed_type: {
                  source: selectedSourceDetails.source,
                  partial: selectedSourceDetails.matchesMultiple,
                },
              }"
              name="form url"
              :debounce="100"
              mode="aggressive"
            >
              <FormGroup>
                <Label :for="`formurl${linkId}`"
                  >Enter {{ selectedSourceDetails.name }} URL</Label
                >
                <Input
                  :id="`formurl${linkId}`"
                  v-model="url"
                  :placeholder="selectedSourceDetails.placeholder"
                  v-bind="ariaInput"
                />
                <FormGroupHelp color="error" v-bind="ariaMsg">{{
                  errors[0]
                }}</FormGroupHelp>
              </FormGroup>
            </ValidationProvider>
            <Button type="submit" :loading="isSaving" :disabled="invalid"
              >Finish Setup</Button
            >
          </template>
        </form>
      </ValidationObserver>
    </CardText>
  </Card>
</template>

<script>
import {
  Card,
  CardText,
  CardTitle,
  Button,
  Divider,
  Input,
  Label,
  FormGroup,
  FormGroupHelp,
  VText,
  IconButton,
  CloseIcon,
  FlexSpacer,
  FORM_ICONS,
} from '@campsite-bio/component-lib';
import { computed, nextTick, ref, watch } from 'vue';
import { FORM_TYPES } from './form-types';
import { useStore } from '../../../compositions';
import { BoxInput, BoxInputGroup } from '../../box-input';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import toast from '../../../utils/toast';
import { reportError } from '@/utils/report-error';

export default {
  components: {
    Card,
    CardText,
    CardTitle,
    Button,
    Divider,
    Input,
    FormGroup,
    FormGroupHelp,
    Label,
    VText,
    FlexSpacer,
    IconButton,
    CloseIcon,
    BoxInput,
    BoxInputGroup,
    ValidationProvider,
    ValidationObserver,
    ...FORM_ICONS,
  },

  props: {
    formOptions: {
      type: Object,
      required: true,
    },

    linkId: {
      type: String,
      required: true,
    },

    isDeleting: Boolean,
  },

  setup(props, { emit }) {
    const store = useStore();
    const selectedSource = ref(null);
    const url = ref(null);
    const isSaving = ref(false);
    const form = ref(null);

    const selectedSourceDetails = computed(() => {
      if (!selectedSource.value) return null;
      return FORM_TYPES.find((type) => type.source === selectedSource.value);
    });

    watch(selectedSource, async () => {
      url.value = null;
      if (form.value) {
        await nextTick();
        form.value.reset();
      }
    });

    async function handleSetup() {
      isSaving.value = true;

      try {
        // Save the result to the feed options
        const { link } = await store.dispatch('links/updateOptions', {
          id: props.linkId,
          name: 'form',
          source: selectedSource.value,
          has_setup: true,
          ...selectedSourceDetails.value.defaultSettings,
        });

        store.commit('links/updateLinkUrl', {
          id: props.linkId,
          value: url.value,
        });
        // Show the link now that it's been setup
        store.commit('links/updateLinkOptions', {
          id: props.linkId,
          value: link.form_options,
          name: 'form_options',
        });
        emit('setup-complete');
      } catch (e) {
        reportError(e);
        toast.error('There was an error trying to get your form source');
      }

      isSaving.value = false;
    }

    return {
      FORM_TYPES,
      selectedSource,
      selectedSourceDetails,
      url,
      handleSetup,
      isSaving,
      form,
    };
  },
};
</script>

<style lang="scss" scoped></style>
